// src/pages/DashboardHome.js
import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Form } from 'react-bootstrap';
import DashboardWidget from '../components/DashboardWidget';
import {
  fetchUserDashboardCounts,
  fetchCustomerDashboardCounts,
  fetchDeviceDashboardCounts,
  fetchEntityCounts,
  fetchClientCounts
} from '../services/api';
// Import icons from the library you installed
import {
  PeopleFill,
  BriefcaseFill,
  BuildingFill
} from 'react-bootstrap-icons';
import { getCurrentUser } from '../services/authService'; 
import { usePermissions } from '../Hooks/usePermission';
import { PERMISSIONS } from '../lib/utils/permissions';
import { PersonBadgeIcon, SmartwatchIcon } from '../components/svg';

const ProfileIcon = ({ size }) => (<svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" viewBox="0 0 16 16"><path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" /><path fillRule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1" /></svg>);

// --- 2. NEW: Helper function to calculate stats ---
/**
 * Counts items in an array based on their status.
 * @param {Array} dataArray - The array of data (e.g., mockUsers)
 * @returns {Object} - An object with { total, stats: [...] }
 */
// --- Calculate Status Counts ---
function getStats(dataArray, type) {
  let total = dataArray ? dataArray.length : 0;

  if (type === 'devices') {
    // Device Logic: Assigned vs Unassigned
    let assigned = 0, unassigned = 0;
    if (total > 0) {
      for (const item of dataArray) {
        const isAssigned = item.patient && item.patient !== 'N/A' && item.patient !== 'Unassigned';
        if (isAssigned) assigned++; else unassigned++;
      }
    }
    return {
      total: total,
      stats: [
        { count: assigned, label: 'Assigned' },
        { count: unassigned, label: 'Unassigned' }
      ]
    };
  } else if (type === 'customers') {
    // Customer Logic: Active vs Deactivated
    let active = 0, deactivated = 0;
    if (total > 0) {
      for (const item of dataArray) {
        const s = String(item.status).toLowerCase();
        if (s === 'active' || s === 'a') active++;
        if (s === 'deactivated' || s === 'd') deactivated++;
      }
    }
    return {
      total: total,
      stats: [
        { count: active, label: 'Active' },
        { count: deactivated, label: 'Deactivated' }
      ]
    };
  } else {
    // Default (Users): Active, Pending, Deactivated
    let active = 0, pending = 0, deactivated = 0;
    if (total > 0) {
      for (const item of dataArray) {
        const s = String(item.status).toLowerCase();
        if (s === 'active' || s === 'a') active++;
        if (s === 'pending' || s === 'r') pending++;
        if (s === 'deactivated' || s === 'd') deactivated++;
      }
    }
    return {
      total: total,
      stats: [
        { count: active, label: 'Active' },
        { count: pending, label: 'Pending' },
        { count: deactivated, label: 'Deactivated' },
      ]
    };
  }
}

function DashboardHome() {
  // Use mock data or fetch real data via API if available for dashboard overview
  const [userStats, setUserStats] = useState({
    total: 0,
    stats: [
      { count: 0, label: 'Active' },
      { count: 0, label: 'Pending' },
      { count: 0, label: 'Deactivated' }
    ]
  });

  const [customerStats, setCustomerStats] = useState({
    total: 0,
    stats: [
      { count: 0, label: 'Active' },
      { count: 0, label: 'Pending' },
      { count: 0, label: 'Deactivated' }
    ]
  });

  const [deviceStats, setDeviceStats] = useState({
    total: 0,
    stats: [
      { count: 0, label: 'Assigned' },
      { count: 0, label: 'Unassigned' }
    ]
  });

  const [entityStats, setEntityStats] = useState({
    total: 0,
    stats: [
      { count: 0, label: 'Active' },
      { count: 0, label: 'Pending' },
      { count: 0, label: 'Deactivated' }
    ]
  });

  const [clientStats, setClientStats] = useState({
    total: 0,
    stats: [
      { count: 0, label: 'Active' },
      { count: 0, label: 'Pending' },
      { count: 0, label: 'Deactivated' }
    ]
  });

  const [currentUser, setCurrentUser] = useState(null);
  const { can } = usePermissions();
  const userRoleCode = JSON.parse(localStorage.getItem('user'))?.role?.code;
  const canViewEntity = can(PERMISSIONS.ENTITY_READ, userRoleCode);
  const canViewClient = can(PERMISSIONS.CLIENT_READ, userRoleCode);
  const canViewUser = can(PERMISSIONS.USER_READ, userRoleCode);
  const canViewCustomer = can(PERMISSIONS.CUSTOMER_READ, userRoleCode);
  const canViewDevice = can(PERMISSIONS.DEVICE_READ_INVENTORY, userRoleCode);
  // Load Dashboard Data
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
    
    const loadStats = async () => {
      // Payload for counts API (empty object to fetch all)
      const payload = {};

      try {
        const [uRes, cRes, dRes, eRes, clRes] = await Promise.allSettled([
          fetchUserDashboardCounts(payload),
          fetchCustomerDashboardCounts(payload),
          fetchDeviceDashboardCounts(payload),
          fetchEntityCounts(),
          fetchClientCounts()
        ]);

        // Process User Response
        if (uRes.status === 'fulfilled' && uRes.value.data?.data) {
          const counts = uRes.value.data.data; // Assuming API returns object { active: 10, ... }
          setUserStats({
            total: (counts.active || 0) + (counts.registered || 0) + (counts.deactivated || 0),
            stats: [
              { count: counts.active || 0, label: 'Active' },
              { count: counts.registered || 0, label: 'Pending' },
              { count: counts.deactivated || 0, label: 'Deactivated' }
            ]
          });
        }

        // Process Customer Response
        if (cRes.status === 'fulfilled' && cRes.value.data?.data) {
          const counts = cRes.value.data.data;
          setCustomerStats({
            total: (counts.active || 0) + (counts.pending || 0) + (counts.deactivated || 0),
            stats: [
              { count: counts.active || 0, label: 'Active' },
              { count: counts.pending || 0, label: 'Pending' }, // Mapped from API
              { count: counts.deactivated || 0, label: 'Deactivated' }
            ]
          });
        }

        // Process Device Response
        if (dRes.status === 'fulfilled' && dRes.value.data?.data) {
          const counts = dRes.value.data.data;
          setDeviceStats({
            total: (counts.assigned || 0) + (counts.unassigned || 0),
            stats: [
              { count: counts.assigned || 0, label: 'Assigned' },
              { count: counts.unassigned || 0, label: 'Unassigned' }
            ]
          });
        }

        // Process Entity Response
        if (eRes.status === 'fulfilled' && eRes.value.data?.data) {
          const counts = eRes.value.data.data;
          setEntityStats({
            total: (counts.active || 0) + (counts.pending || 0) + (counts.deactivated || 0),
            stats: [
              { count: counts.active || 0, label: 'Active' },
              { count: counts.pending || 0, label: 'Pending' },
              { count: counts.deactivated || 0, label: 'Deactivated' }
            ]
          });
        }

        // Process Client Response
        if (clRes.status === 'fulfilled' && clRes.value.data?.data) {
          const counts = clRes.value.data.data;
          setClientStats({
            total: (counts.active || 0) + (counts.pending || 0) + (counts.deactivated || 0),
            stats: [
              { count: counts.active || 0, label: 'Active' },
              { count: counts.pending || 0, label: 'Pending' },
              { count: counts.deactivated || 0, label: 'Deactivated' }
            ]
          });
        }

      } catch (err) {
        console.error("Failed to load dashboard stats", err);
      }
    };

    loadStats();
  }, []);

  const roleCode = currentUser?.role?.code;
  // const accountType = currentUser?.entityMaster?.account_type;

  // Logic: Only USER role is restricted from User Management widget
  const showUserWidget = roleCode !== 'USER';

  return (
    <>
      {/* 1. Date Filter Row */}
      <Row className="mb-4 justify-content-end align-items-center g-3">
        {/* <Col xs="auto">
          <Button variant="outline-primary" size="sm">Last 30 Days</Button>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <Form.Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>Start Date:</Form.Label>
          <Form.Control type="date" size="sm" defaultValue="2025-10-09" />
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <Form.Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>End Date:</Form.Label>
          <Form.Control type="date" size="sm" defaultValue="2025-11-07" />
        </Col> */}
      </Row>

      {/* 2. Widget Grid */}
      {/* This grid is responsive: 1 col on small, 2 on medium, 3 on large screens */}
      <Row xs={1} md={2} lg={3} className="g-4">
        {canViewEntity && (
          <DashboardWidget
            title="Entities"
            icon={<BuildingFill size={50} />}
            totalCount={entityStats.total}
            stats={entityStats.stats}
            linkTo="/entity"
          />
        )}

        {canViewClient && (
          <DashboardWidget
            title="Clients"
            icon={<BriefcaseFill size={50} />}
            totalCount={clientStats.total}
            stats={clientStats.stats}
            linkTo="/client"
          />
        )}

        {showUserWidget && canViewUser && (
          <DashboardWidget
            title="Admin Users"
            icon={<PeopleFill size={50} />}
            totalCount={userStats.total}
            stats={userStats.stats}
            linkTo="/users"
          />
        )}

        {canViewCustomer && (
          <DashboardWidget
            title="Device Users"
            icon={<PersonBadgeIcon size={50} />}
            totalCount={customerStats.total}
            stats={customerStats.stats}
            linkTo="/customers"
          />
        )}

        {canViewDevice && (
          <DashboardWidget
            title="Devices"
            icon={<SmartwatchIcon size={50} />}
            totalCount={deviceStats.total}
            stats={deviceStats.stats}
            linkTo="/devices"
          />
        )}

        {/* You can easily add more widgets from your image here */}
      </Row>
    </>
  );
}

export default DashboardHome;